/**
 * /admin/voiture — Gestion du contenu voiture (import DB + état).
 * Le bouton d'import 103 articles est ICI, très visible.
 */
import React, { useEffect, useState } from 'react';
import { Car, Upload, CheckCircle2, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

export default function AdminVoiturePage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { document.title = 'Voiture · Admin'; }, []);

  const handleImport = async () => {
    if (loading) return;
    if (!window.confirm('Importer les 103 articles voiture en base ? L’opération est idempotente : les articles déjà présents seront skippés.')) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.boSeedVoitureContent();
      setResult(r);
      toast.success('Contenu voiture importé avec succès');
    } catch (e) {
      const msg = e.message || 'Erreur lors de l’import';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-voiture-page">
      <div className="mb-6">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-500 mb-1 flex items-center gap-2">
          <Car size={12} /> Admin · Contenu voiture
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Gestion du contenu /voiture
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          La page publique <a href="/voiture" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">/voiture</a> affiche 103 articles importés de <strong>codevoiture.fr</strong>. Si elle est vide, utilisez le bouton ci-dessous pour peupler la DB en un clic.
        </p>
      </div>

      {/* Bouton d'import géant */}
      <div className="rounded-2xl border-2 border-blue-500/30 bg-gradient-to-br from-blue-600/10 to-blue-500/5 p-6 sm:p-8 mb-6" data-testid="import-voiture-panel">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-900/40 flex-shrink-0">
            <Car className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Importer les 103 articles voiture
            </h2>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Charge en base : 14 enquêtes magazine + 9 dossiers ETM + 24 satellites + tous les articles voiture détaillés. <strong>Idempotent</strong> — vous pouvez cliquer plusieurs fois sans risque.
            </p>
            <button
              onClick={handleImport}
              disabled={loading}
              data-testid="btn-import-voiture"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-sm shadow-lg shadow-blue-900/30 transition disabled:opacity-60 disabled:cursor-wait"
            >
              {loading ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Import en cours…</>
              ) : (
                <><Upload className="w-4 h-4" /> Lancer l’import</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Résultat */}
      {result && (
        <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 p-5 mb-6" data-testid="voiture-import-result">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-black text-emerald-700 dark:text-emerald-300 mb-1">Import terminé avec succès</div>
              <div className="text-sm text-foreground/80 mb-3">
                Rechargez la page <a href="/voiture" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline inline-flex items-center gap-1">/voiture <ExternalLink className="w-3 h-3" /></a> pour vérifier que les 9 tuiles ETM et les articles apparaissent.
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Détails du rapport d’import</summary>
                <pre className="mt-2 p-3 rounded-lg bg-black/40 text-emerald-200 text-[11px] overflow-auto max-h-64">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl border-2 border-red-500/40 bg-red-500/5 p-5 mb-6" data-testid="voiture-import-error">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-black text-red-600 dark:text-red-300 mb-1">Erreur lors de l’import</div>
              <div className="text-sm text-foreground/80">{error}</div>
              <div className="text-xs text-muted-foreground mt-2">Vérifiez que vous êtes connecté en tant qu’<code className="bg-muted px-1 rounded">admin</code> et que le fichier <code className="bg-muted px-1 rounded">backend/data/voiture_content_seed.json.gz</code> existe.</div>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="rounded-xl border border-border bg-card/50 p-5 text-sm">
        <div className="font-black text-foreground mb-2">À propos du contenu voiture</div>
        <ul className="space-y-1.5 text-muted-foreground text-xs">
          <li>• Le contenu est extrait de la plateforme sœur <strong>codevoiture.fr</strong> (même équipe ECSR).</li>
          <li>• Les 9 tuiles ETM (Enseignements Théoriques Méthodologiques) pointent vers <strong>codevoiture.fr/apprendre/{'{slug}'}</strong> pour le moment.</li>
          <li>• L’import fusionne intelligemment : les articles déjà en base sont conservés, seuls les nouveaux sont ajoutés.</li>
          <li>• Volume total : ~131 KB de données JSON compressées (gzip).</li>
        </ul>
      </div>
    </div>
  );
}
